import React, { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";

/** --- TWEAKS --- */
const SHOW_AFTER_MS = 1800;       // delay before appearing
const DURATION_MS   = 11000;      // time to cross the screen
const REPEAT_EVERY_MS = 0;        // 0 = once; set 60000 = every minute
const BOTTOM_OFFSET_PX = 80;      // keep above any sticky bars
const STORAGE_KEY = "mascotDismissed";

/** --- Motion --- */
const bob = keyframes`
  0%,100% { transform: translateY(0) }
  50%     { transform: translateY(-6px) }
`;
const blink = keyframes`
  0%, 92%, 100% { transform: scaleY(1) }
  95%           { transform: scaleY(0.1) }
`;
const step = keyframes`
  0% { transform: rotate(12deg) }
  50%{ transform: rotate(-12deg) }
  100% { transform: rotate(12deg) }
`;
const swing = keyframes`
  0%,100%{ transform: rotate(-3deg) }
  50%    { transform: rotate(3deg) }
`;
const leftToRight = keyframes`
  0%   { transform: translateX(-25vw) }
  100% { transform: translateX(120vw) }
`;
const rightToLeft = keyframes`
  0%   { transform: translateX(120vw) scaleX(-1) }
  100% { transform: translateX(-25vw) scaleX(-1) }
`;

// Neon glow animations
const neonGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
  }
  50% {
    box-shadow: 0 0 10px currentColor, 0 0 20px currentColor, 0 0 30px currentColor, 0 0 40px currentColor;
  }
`;

const neonPulse = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 5px currentColor) drop-shadow(0 10px 22px rgba(0,0,0,.25));
  }
  50% {
    filter: drop-shadow(0 0 10px currentColor) drop-shadow(0 0 20px currentColor) drop-shadow(0 10px 22px rgba(0,0,0,.25));
  }
`;

/** --- Shell --- */
const Wrap = styled.div`
  position: fixed; left:0; right:0; bottom:${BOTTOM_OFFSET_PX}px; z-index:60;
  pointer-events:none;
  contain: layout paint;
  @media (min-width:768px){ bottom:${BOTTOM_OFFSET_PX + 8}px; }
  @media (prefers-reduced-motion: reduce){ display:none; }
`;
const Track = styled.div` width:100%; display:grid; place-items:center; `;
const Walker = styled.div`
  display:inline-grid; grid-auto-flow:column; align-items:end; gap:16px;
  filter: drop-shadow(0 10px 22px rgba(0,0,0,.25));
  animation: ${p=>p.dir==="rtl" ? rightToLeft : leftToRight} ${DURATION_MS}ms linear forwards;
`;

/** --- Character --- */
const Mascot = styled.div`
  position: relative; width: 120px; height: 110px;
  animation: ${bob} 1500ms ease-in-out infinite;
`;

/* SVG container so we can style bits */
const Castle = styled.svg`
  width: 120px; height: 110px; display:block;

  .body { fill: url(#gBody); stroke:#0f172a10; stroke-width:1; }
  .tower { fill: url(#gTower); }
  .roof  { fill: url(#gRoof); }
  .flag  { fill: #fde047; }
  .door  { fill: #0f172a; }
  .eyeW  { fill: #fff; }
  .eyeP  { fill: #0f172a; transform-origin: center; animation:${blink} 4200ms infinite; }
  .smile { stroke:#0f172a; stroke-width:3; stroke-linecap:round; fill:none; }
`;

/** Legs & pole (outside SVG for easy motion) */
const Leg = styled.div`
  width: 14px; height: 22px; border-radius: 4px;
  background: #0f172a;
  transform-origin: top center;
  animation: ${step} 650ms ease-in-out infinite;
  ${p=>p.second && css`animation-delay: 325ms;`}
  box-shadow: 0 6px 10px rgba(0,0,0,.2);
`;
const Shoe = styled.div`
  width: 20px; height: 8px; background:#111827; border-radius: 8px; margin-top: -2px;
  box-shadow: 0 2px 4px rgba(0,0,0,.25) inset;
`;

/** Sign & pole */
const Pole = styled.div`
  width: 10px; height: 64px; background:#6b7280; border-radius: 6px; align-self:flex-end;
  box-shadow: 0 4px 10px rgba(0,0,0,.15) inset;
`;
const SignWrap = styled.div`
  position: relative; align-self:flex-end; pointer-events:auto;
  animation: ${swing} 1800ms ease-in-out infinite;
`;
const SignBtn = styled.button`
  all: unset;
  display:inline-grid; place-items:center; cursor:pointer; user-select:none;
  min-width: 130px; min-height: 44px;
  padding: 6px 12px; border-radius: 12px;
  font-weight: 900; letter-spacing:.4px; text-transform: uppercase;
  background: linear-gradient(90deg, #39ff14, #00ccff);
      color:#0a0f1c; box-shadow: 0 0 20px rgba(57, 255, 20, 0.45);
  border: 2px solid #39ff14;
  transition: all 0.3s ease;
  
  &:hover{ 
    filter: brightness(1.05); 
    box-shadow: 0 0 30px rgba(57, 255, 20, 0.6), 0 0 60px rgba(57, 255, 20, 0.3);
    transform: translateY(-2px);
  }
  &:active{ transform: translateY(1px); }
  
  /* Counter the scaleX(-1) when mascot goes right-to-left */
  .rtl & {
    transform: scaleX(-1);
  }
`;
const Strings = styled.div`
  position:absolute; left:-8px; top:-8px; right:-8px; height: 16px; pointer-events:none;
  &::before, &::after{
    content:""; position:absolute; top:0; width: 38%; height: 16px;
    border-top: 3px dashed #6b7280;
  }
  &::before{ left: 8px; transform: rotate(-8deg); }
  &::after{ right: 8px; transform: rotate(8deg); }
`;

/** Close button */
const Close = styled.button`
  pointer-events:auto; position:absolute; right:-8px; top:-8px;
  width: 28px; height: 28px; border:0; border-radius: 999px;
  background:#111827; color:#39ff14; font-weight:900; line-height:1;
  box-shadow: 0 4px 10px rgba(0,0,0,.2);
  border: 2px solid #39ff14;
  animation: ${neonGlow} 1s ease-in-out infinite alternate;
  
  &:hover {
    background:#39ff14;
    color:#111827;
    box-shadow: 0 0 15px #39ff14;
  }
`;

/** --- Component --- */
export default function MascotWalker({ enable=true, showAfterMs=SHOW_AFTER_MS, repeatEveryMs=REPEAT_EVERY_MS }){
  const [visible, setVisible] = useState(false);
  const [dir] = useState(Math.random()>0.5 ? "rtl" : "ltr");
  const nav = useNavigate();
  const { pathname } = useLocation();

  const hidden = pathname.startsWith("/admin") || pathname.startsWith("/manage");

  useEffect(()=>{
    if (!enable || hidden) return;
    if (localStorage.getItem(STORAGE_KEY) === "1") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) return;
    let t1 = setTimeout(()=> setVisible(true), showAfterMs);
    let t2; if (repeatEveryMs>0){ t2 = setInterval(()=> setVisible(v=>!v), repeatEveryMs); }
    return ()=>{ clearTimeout(t1); if(t2) clearInterval(t2); };
  }, [enable, hidden, showAfterMs, repeatEveryMs]);

  if (!enable || hidden || !visible) return null;

  const go = () => nav("/booking");
  const dismiss = () => { localStorage.setItem(STORAGE_KEY,"1"); setVisible(false); };

  return (
    <Wrap aria-hidden>
      <Track>
        <Walker dir={dir} className={dir === "rtl" ? "rtl" : ""}>
          {/* Character */}
          <Mascot>
            <Castle viewBox="0 0 120 110" aria-hidden>
              <defs>
                <linearGradient id="gBody" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#39ff14"/><stop offset="100%" stopColor="#00ccff"/>
                </linearGradient>
                <linearGradient id="gTower" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#00ccff"/><stop offset="100%" stopColor="#00e6e6"/>
                </linearGradient>
                <linearGradient id="gRoof" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#ff0080"/><stop offset="100%" stopColor="#ff40a0"/>
                </linearGradient>
              </defs>

              {/* towers */}
              <rect className="tower" x="4"  y="22" width="24" height="46" rx="6" />
              <rect className="tower" x="92" y="22" width="24" height="46" rx="6" />
              <path className="roof" d="M16 10 L28 22 L4 22 Z" />
              <path className="roof" d="M104 10 L116 22 L92 22 Z" />
              <rect className="flag" x="26" y="8" width="8" height="3" rx="1" />
              <rect className="flag" x="86" y="8" width="8" height="3" rx="1" />

              {/* body */}
              <rect className="body" x="16" y="28" width="88" height="60" rx="14" />
              {/* door */}
              <rect className="door" x="56" y="62" width="16" height="20" rx="8" />

              {/* eyes */}
              <g transform="translate(46,46)">
                <circle className="eyeW" cx="0" cy="0" r="8"/>
                <circle className="eyeP" cx="0" cy="0" r="4"/>
              </g>
              <g transform="translate(78,46)">
                <circle className="eyeW" cx="0" cy="0" r="8"/>
                <circle className="eyeP" cx="0" cy="0" r="4"/>
              </g>
              {/* smile */}
              <path className="smile" d="M48 56 Q62 68 76 56" />
            </Castle>
          </Mascot>

          {/* Sign (with pole & strings) */}
          <Pole />
          <SignWrap>
            <Strings />
            <SignBtn onClick={go} aria-label="Rent Now">
              {dir === "rtl" ? "Rent Now" : "Rent Now"}
            </SignBtn>
          </SignWrap>

          {/* Legs */}
          <div style={{display:"grid", gap:4, alignSelf:"end", transform:"translateY(6px)"}}>
            <Leg /><Shoe />
          </div>
          <div style={{display:"grid", gap:4, alignSelf:"end", transform:"translateY(6px)"}}>
            <Leg second /><Shoe />
          </div>

          {/* Dismiss */}
          <Close title="Hide mascot" onClick={dismiss}>×</Close>
        </Walker>
      </Track>
    </Wrap>
  );
}
